import React, { useContext, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import BoxHistory from '../../../components/assessmentTimeline/BoxHistory';
import SendNotificationButton from '../../../components/button/SendNotificationButton';
import { AssessmentConstants } from '../../../shared/Constants';
import AssessmentNotify from '../../../graphql/AssessmentNotify';
import { Loading } from '../../../components/Loading';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  resetModalHeader: {
    id: 'assessment.modal.reset.status.header',
    defaultMessage: 'Assessment status reset',
  },
  cancel: {
    id: 'actions.cancel',
    defaultMessage: 'Cancel',
  },
  ok: {
    id: 'actions.ok',
    defaultMessage: 'OK',
  },
});

const AsmtTimelineBox = ({ asmt }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const [isEmailSending, setIsEmailSending] = useState(false);
  const [isResetStatusDecline, setIsResetStatusDecline] = useState(false);

  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const sendInviteEmail = async () => {
    if (asmt) {
      setIsEmailSending(true);
      try {
        await AssessmentNotify.sendInvitation(asmt.id, currentAccount);
        growlActionSuccess(growl, formatMessage(messages.successNoti));
      } catch (errors) {
        growlErrors(growl, errors);
      } finally {
        setIsEmailSending(false);
      }
    }
  };

  const sendRemindingEmail = async () => {
    if (asmt) {
      setIsEmailSending(true);
      try {
        await AssessmentNotify.sendRemind(asmt.id, currentAccount);
        growlActionSuccess(growl, formatMessage(messages.successNoti));
      } catch (errors) {
        growlErrors(growl, errors);
      } finally {
        setIsEmailSending(false);
      }
    }
  };

  const acceptResetStatus = async () => {
    if (asmt) {
      setIsResetStatusDecline(false);
      setIsEmailSending(true);
      try {
        await AssessmentNotify.resetAssessmentDecline(currentAccount, asmt.id);
        growlActionSuccess(growl, formatMessage(messages.successNoti));
      } catch (errors) {
        growlErrors(growl, errors);
      } finally {
        setIsEmailSending(false);
      }
    }
  };

  if (!asmt) {
    return <Loading />;
  }

  const footer = (
    <div>
      <Button
        label={formatMessage(messages.cancel)}
        className="p-button-secondary"
        onClick={() => setIsResetStatusDecline(false)}
      />
      <Button
        label={formatMessage(messages.ok)}
        className="p-button-warning"
        disabled={subscriptionIsExpired}
        onClick={acceptResetStatus}
      />
    </div>
  );

  return (
    <>
      <div className="panel-body panel-white">
        <div className="timeline">
          {asmt.submittedOn ? (
            <div className="timeline-item">
              <div className="timeline-header" />
              <div className="timeline-body">
                <BoxHistory status={AssessmentConstants.ASMT_STATUS.SUBMITTED} date={asmt.submittedOn} />
              </div>
            </div>
          ) : null}

          {asmt.status === AssessmentConstants.ASMT_STATUS.EXPIRED ? (
            <div className="timeline-item">
              <div className="timeline-header" />
              <div className="timeline-body">
                <BoxHistory status={AssessmentConstants.ASMT_STATUS.EXPIRED} date={asmt.expiredOn} />
              </div>
            </div>
          ) : null}

          {asmt.status === AssessmentConstants.ASMT_STATUS.DECLINED ? (
            <div className="timeline-item">
              <div className="timeline-header" />
              <div className="timeline-body">
                <BoxHistory status={AssessmentConstants.ASMT_STATUS.DECLINED} date={asmt.declinedOn} />
              </div>
              <SendNotificationButton
                assessment={asmt}
                isDisabled={isEmailSending}
                actionMethod={() => setIsResetStatusDecline(true)}
              />
            </div>
          ) : null}

          {asmt.startedOn ? (
            <div className="timeline-item">
              <div className="timeline-header" />
              <div className="timeline-body">
                <BoxHistory status={AssessmentConstants.ASMT_STATUS.STARTED} date={asmt.startedOn} />
              </div>

              {asmt.status === AssessmentConstants.ASMT_STATUS.STARTED ? (
                <SendNotificationButton
                  assessment={asmt}
                  isDisabled={isEmailSending}
                  actionMethod={sendRemindingEmail}
                />
              ) : null}
            </div>
          ) : null}

          {asmt.invitedOn ? (
            <div className="timeline-item">
              <div className="timeline-header" />
              <div className="timeline-body">
                <BoxHistory status={AssessmentConstants.ASMT_STATUS.INVITED} date={asmt.invitedOn} />
              </div>

              {asmt.status === AssessmentConstants.ASMT_STATUS.INVITED ? (
                <SendNotificationButton
                  assessment={asmt}
                  isDisabled={isEmailSending}
                  actionMethod={sendRemindingEmail}
                />
              ) : null}
            </div>
          ) : null}

          {asmt.createdOn ? (
            <div className="timeline-item">
              <div className="timeline-header" />
              <div className="timeline-body">
                <BoxHistory status={AssessmentConstants.ASMT_STATUS.NEW} date={asmt.createdOn} />
              </div>

              {asmt.status === AssessmentConstants.ASMT_STATUS.NEW ? (
                <SendNotificationButton assessment={asmt} isDisabled={isEmailSending} actionMethod={sendInviteEmail} />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <Dialog
        header={formatMessage(messages.resetModalHeader)}
        footer={footer}
        visible={isResetStatusDecline}
        style={{ width: '50vw' }}
        modal
        onHide={() => setIsResetStatusDecline(false)}
      >
        <FormattedMessage id="mes.cofirm_reset_status" defaultMessage='Clicking OK will reset the status to "INVITED".'>
          {(text) => <p className="sentence-capitalized">{text}</p>}
        </FormattedMessage>
      </Dialog>
    </>
  );
};

AsmtTimelineBox.propTypes = {
  asmt: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AsmtTimelineBox;
